import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter as _filter, get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import {
  NamedInsured,
  RiskModel,
  UnderwritingQuestionModel,
  VehicleOwnership
} from '@amfam/policy/models';
import { TPIIndicator, UnderwritingQuestion } from '@amfam/policy/vehicle/data-access';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  ErrorObj,
  LoadingSpinnerService,
  Option,
  PageTitleService,
  ValidationService
} from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';
import { QuickQuoteFeatureService } from '../services/quick-quote-feature.service';

@Component({
  selector: 'ds-underwriting-question-list',
  templateUrl: './underwriting-question-list.component.html',
  styleUrls: ['./underwriting-question-list.component.scss'],
  standalone: false
})
export class UnderwritingQuestionListComponent implements OnInit, AfterViewInit, OnDestroy {
  exitFlowModalId = this.modalService.createModalId('exitFlowModalId');
  underwritingQuestions: Option[] = [
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsOwner')['key'],
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsOwner')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsOwner')['helpText']
        }
      ],
      mappedQuestion: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsOwner')[
        'value'
      ],
      id: 1,
      code: VehicleOwnership.COOWN
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsBusinessUse')['key'],
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsBusinessUse')[
        'key'
      ],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsBusinessUse')[
            'helpText'
          ]
        }
      ],
      mappedQuestion: this.copyService.getCopy(
        'policy.newVehicleUnderWritingQuestionIsBusinessUse'
      )['value'],
      id: 2,
      code: VehicleOwnership.ISBUSINESS
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasLoan')['key'],
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasLoan')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasLoan')['helpText']
        }
      ],
      mappedQuestion: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasLoan')[
        'key'
      ],
      id: 3,
      code: VehicleOwnership.LOANLEASE
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionNoneOfThese'),
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionNoneOfThese'),
      helpText: [{ text: '' }],
      mappedQuestion: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionNoneOfThese'),
      id: 4,
      code: VehicleOwnership.NONE
    }
  ];
  questions: Option[] = [
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionOwnVehicle')['key'],
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionOwnVehicle')['key'],
      helpText: undefined,
      mappedQuestion: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionOwnVehicle')[
        'key'
      ],
      id: '1',
      code: VehicleOwnership.OWN
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALoan')['key'],
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALoan')['key'],
      helpText: undefined,
      mappedQuestion: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALoan')[
        'key'
      ],
      id: '2',
      code: VehicleOwnership.LOAN
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALease')['key'],
      question: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALease')['key'],
      helpText: undefined,
      mappedQuestion: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALease')[
        'key'
      ],
      id: '3',
      code: VehicleOwnership.LEASE
    }
  ];

  showTPIQuestion = false;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();
  underWritingQuestionsForm: UntypedFormGroup;
  underWritingQuestionsCtrl: AbstractControl;
  private tpi: NamedInsured;
  policyNumber: string;
  replaceVin: string;
  errorList: Array<ErrorObj> = [];
  errorMessage: string;
  private replaceVehicleRisks$: Observable<RiskModel[]> =
    this.quickQuoteFeatureFacade.replaceVehicleRisks$;
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  anyTpisInPolicy = false;

  constructor(
    private store: Store<any>,
    private formBuilder: UntypedFormBuilder,
    public spinner: LoadingSpinnerService,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    public roleService: ImpersonateRolesService,
    public copyService: CopyService,
    private quickQuoteFeatureService: QuickQuoteFeatureService,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private modalService: DsModalService
  ) {}

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');
    this.buildForm();

    this.replaceVehicleRisks$
      .pipe(
        filter(vehicles => vehicles.length > 0),
        take(1),
        withLatestFrom(this.quickQuoteFeatureFacade.vehicleId$)
      )
      .subscribe(([vehicles, vehicleId]) => {
        if (vehicles) {
          const matchingVehicle = vehicles.find(vehicle => {
            return _get(vehicle, 'id') === vehicleId;
          });
          this.tpi = _filter(matchingVehicle.vehicleRoles, { roleType: 'ThirdPartyInterest' })[0];
          this.anyTpisInPolicy = vehicles.some(vehicle =>
            vehicle.vehicleRoles.some(role => role.roleType === 'ThirdPartyInterest')
          );

          if (this.quickQuoteFeatureService.getAdvancePolicyEligibility(matchingVehicle)) {
            this.policyNumber = matchingVehicle.policyNumber;
            this.replaceVin = matchingVehicle.vin;
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/agent-help`
                ],
                extras: {
                  replaceUrl: true
                }
              })
            );
          }
        }
      });
  }

  ngAfterViewInit() {
    this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_UW_QUESTIONS);
  }

  private buildForm() {
    this.underWritingQuestionsForm = this.formBuilder.group(
      {
        option: ['', Validators.compose([Validators.required])]
      },
      { validators: ValidationService.minimumCheckedCheckboxCheckedValidator }
    );
  }

  answersSubmit() {
    const answers = this.underWritingQuestionsForm.value;
    //const eligibleToReplaceVehicle = answers.option[3] === true;

    combineLatest([
      this.quickQuoteFeatureFacade.vehicleCriteria$,
      this.quickQuoteFeatureFacade.vehiclePolicies$,
      this.quickQuoteFeatureFacade.vehicleId$
    ])
      .pipe(
        map(([vehicleSelectionDetails, policies, vehicleId]) => ({
          vehicleSelectionDetails,
          policies,
          vehicleId
        })),
        take(1)
      )
      .subscribe(({ vehicleSelectionDetails, policies, vehicleId }) => {
        const isNewVehicleCoverage = policies
          .find(policy => policy.policyNumber === this.policyNumber)
          .vehicles.find(vehicle => vehicle.vehicleId === vehicleId)
          .vehicleCoverages.some(
            coverage => coverage.code === this.getCoverageCodes(coverage.code)
          );

        if (!isNewVehicleCoverage) {
          this.getQuoteRequest(vehicleId);
        } else {
          const vehicleCriteriaOptions = {
            copyTPIIndicator: TPIIndicator.NO,
            underWritingQuestions: this.getUnderWritingQuestionsForPayload(<
              UnderwritingQuestionModel[]
            >[...this.questions]),
            TPIName: ''
          };

          this.quickQuoteFeatureFacade.setVehicleCriteria(vehicleCriteriaOptions);
          // Can't bind online take them to a offine bind page to let them notify their agent.
          this.store.dispatch(
            fromRouterActions.Go({
              path: [
                `/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/agent-help`
              ],
              extras: {
                replaceUrl: true
              }
            })
          );
        }
      });
  }

  private getQuoteRequest(vehicleId) {
    const vehicleCriteriaOptions = {
      copyTPIIndicator: TPIIndicator.NO,
      underWritingQuestions: this.getUnderWritingQuestionsForPayload(<UnderwritingQuestionModel[]>[
        ...this.questions
      ]),
      TPIName: ''
    };
    this.quickQuoteFeatureFacade.setVehicleCriteria(vehicleCriteriaOptions);
    this.quickQuoteFeatureFacade.setUnderWritingQuestions(
      this.getUnderWritingQuestionsForPayload(<UnderwritingQuestionModel[]>[...this.questions])
    );
    const hasLoanOrLease = this.hasLoanOrLease();
    if (!hasLoanOrLease || (hasLoanOrLease && !this.anyTpisInPolicy)) {
      this.spinner.start({
        secondaryMessage: `We're calculating your quote. This may take a moment.`,
        blockActions: true,
        maxWidth: '338'
      });
      this.quickQuoteFeatureFacade.getQuote('quotes');
    } else
      this.store.dispatch(
        fromRouterActions.Go({
          path: [`/policies/risk-selection/replace-vehicle-quick-quote/${vehicleId}/loan-info`]
        })
      );
  }

  cancel() {
    this.modalService.open(this.exitFlowModalId);
    this.quickQuoteFeatureFacade.sendAnalytics(
      AnalyticsEventType.REPLACE_VEHICLE_UW_DETAILS_CANCEL
    );
  }

  stay() {
    this.modalService.close(this.exitFlowModalId);
  }

  sendToAgent() {
    this.spinner.start({ blockActions: true });
    this.quickQuoteFeatureFacade.setOperation(
      ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_UW_SEND_TO_AGENT
    );
    this.quickQuoteFeatureFacade.sendToAgentOnCancel();
  }

  navigateToOverview() {
    // Route to history -1 ??
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  /**
   * @author: Abhishek Singh
   * @param questions
   * @description: Get the question and answers in the format which is expected by the API.
   */
  private getUnderWritingQuestionsForPayload(
    questions: UnderwritingQuestionModel[],
    mapQuestion?: boolean
  ): UnderwritingQuestion[] {
    const underWritingQuestionsForPayload: UnderwritingQuestion[] = [];
    for (const questionItem of this.underwritingQuestions) {
      if (questionItem.question) {
        underWritingQuestionsForPayload.push({
          question: mapQuestion ? questionItem.mappedQuestion : questionItem.question,
          answer: this.getAnswer(questionItem)
        });
      }
    }
    return underWritingQuestionsForPayload;
  }

  hasLoanOrLease(): boolean {
    const selectedAnswer = this.underWritingQuestionsForm.value;
    return (
      selectedAnswer?.option?.code === VehicleOwnership.LEASE ||
      selectedAnswer?.option?.code === VehicleOwnership.LOAN
    );
  }

  getAnswer(item: Option): string {
    if (!item) return String(false);

    const hasLoanOrLease = this.hasLoanOrLease();

    switch (item.code) {
      case VehicleOwnership.LOANLEASE:
        return String(hasLoanOrLease);

      case VehicleOwnership.NONE:
        return String(!hasLoanOrLease);

      case VehicleOwnership.COOWN:
      case VehicleOwnership.ISBUSINESS:
        return String(false);

      default:
        break;
    }
  }

  /**
   * @param code
   * @description Checks if the code passed in the params matches one of the New car coverage codes.
   */
  private getCoverageCodes(code: string) {
    if (code === 'PANewCarReplaceCov_af' || code === 'PAAgreedValAmtCov_af') {
      return code;
    }
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }
}
